import { createRootRoute, createRoute, Outlet, redirect } from '@tanstack/react-router'
import { LoginPage } from '@/pages/auth/login'

import { DashboardPage } from '@/pages/dashboard'
import { DestinationFormPage } from '@/pages/destination/form'
import { DestinationListPage } from '@/pages/destination/list'
import { NotFoundPage } from '@/pages/not-found'
import { PoiCategoryCreatePage } from '@/pages/poi-category/create'
import { PoiCategoryEditPage } from '@/pages/poi-category/edit'
import { PoiCategoryListPage } from '@/pages/poi-category/list'

import { PoiFormPage } from '@/pages/poi/form'
import { PoiListPage } from '@/pages/poi/list'
import { SurveyListPage } from '@/pages/survey/list'
import { SurveyQuestionPage } from '@/pages/survey/question'
import { SurveyResponsesPage } from '@/pages/survey/responses'
import { SurveyVersionShellPage } from '@/pages/survey/version'
import TenantPage from '@/pages/tenant'
import { TenantDestinationPage } from '@/pages/tenant-destination'
import { TenantDetailPage } from '@/pages/tenant/detail'
import { TicketDetailPage } from '@/pages/ticket/detail'
import { TicketListPage } from '@/pages/ticket/list'
import { TicketConfigFormPage } from '@/pages/ticket-config/form'
import { TicketConfigListPage } from '@/pages/ticket-config/list'
import { TicketFeatureListPage } from '@/pages/ticket-feature/list'
import { TourFormPage } from '@/pages/tour/form'
import { TourListPage } from '@/pages/tour/list'
import UserListPage from '@/pages/user'
import { UserDetailPage } from '@/pages/user/detail'
import { VoucherFormPage } from '@/pages/voucher/form'
import { VoucherListPage } from '@/pages/voucher/list'
import { AdminPathSegment, AdminScopeType } from '@/shared/constants/admin-scope'
import {
  getAdminHomePathForScope,
  getAdminScopeForUser,
  getAdminScopeFromPath,
  getSuperAdminHomePath,
  getSuperLoginPath,
  getTenantAdminHomePath,
  getTenantLoginPath,
  isSuperAdminRole,
  isTenantAdminRole,
} from '@/shared/libs/admin-routes'
import { useAuthStore } from '@/shared/store/auth-store'
import { AdminLayout } from '@/widgets/admin-layout'
import { BackgroundJobsPanel } from '@/widgets/background-jobs-panel'

/**
 * Trang đích sau khi đã xác định user đăng nhập: home theo scope (super / tenant).
 *
 * @returns Path home admin tương ứng user; nếu không suy ra được scope thì về login super.
 */
function getAuthenticatedHomePath(): string {
  const state = useAuthStore.getState()
  const scope = getAdminScopeForUser(state.user, state.activeTenantSlug)
  return scope ? getAdminHomePathForScope(scope) : getSuperLoginPath()
}

/**
 * Bảo vệ khu vực super: chỉ super admin được vào; các role khác bị đẩy về home đúng scope hoặc login.
 *
 * @throws `redirect` — khi chưa đăng nhập, hoặc không phải super admin.
 */
function validateSuperAccess(): void {
  const state = useAuthStore.getState()
  if (!state.isAuthenticated) {
    throw redirect({ to: getSuperLoginPath() })
  }
  if (!isSuperAdminRole(state.user?.role)) {
    const scope = getAdminScopeForUser(state.user, state.activeTenantSlug)
    throw redirect({ to: scope ? getAdminHomePathForScope(scope) : getSuperLoginPath() })
  }
}

/**
 * Bảo vệ khu vực tenant trong URL (`/{tenantSlug}/admin/...`).
 * Đồng bộ `activeTenantSlug` với tenant trên path; chặn super admin (đẩy về `/super`) và user sai tenant.
 *
 * @param pathname - `location.pathname` của request hiện tại.
 * @throws `redirect` — khi path không phải tenant scope, chưa login, sai role, hoặc slug tenant không khớp session.
 */
function validateTenantAccess(pathname: string): void {
  const state = useAuthStore.getState()
  const scope = getAdminScopeFromPath(pathname)
  if (!scope || scope.type !== AdminScopeType.Tenant) {
    throw redirect({ to: getSuperLoginPath() })
  }
  if (!state.isAuthenticated) {
    throw redirect({ href: getTenantLoginPath(scope.tenantSlug) })
  }
  if (isSuperAdminRole(state.user?.role)) {
    throw redirect({ to: getSuperAdminHomePath() })
  }
  if (!isTenantAdminRole(state.user?.role)) {
    throw redirect({ href: getTenantLoginPath(scope.tenantSlug) })
  }

  const currentTenantSlug = state.activeTenantSlug ?? state.user?.tenantSlug
  if (currentTenantSlug && currentTenantSlug !== scope.tenantSlug) {
    throw redirect({ to: getTenantAdminHomePath(currentTenantSlug) })
  }
  state.setActiveTenantSlug(scope.tenantSlug)
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <BackgroundJobsPanel />
    </>
  ),
  notFoundComponent: NotFoundPage,
})

const superLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/super/login',
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ href: getAuthenticatedHomePath() })
    }
  },
  component: LoginPage,
})

const tenantLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$tenantSlug/login',
  beforeLoad: ({ params }) => {
    if (!params.tenantSlug || params.tenantSlug === AdminPathSegment.Super) {
      throw redirect({ to: getSuperLoginPath() })
    }
  },
  component: LoginPage,
})

const superProtectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'super-protected',
  beforeLoad: validateSuperAccess,
  component: () => (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  ),
})

const tenantProtectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'tenant-protected',
  beforeLoad: ({ location }) => validateTenantAccess(location.pathname),
  component: () => (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  ),
})

const superDashboardRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super',
  component: DashboardPage,
})
const superTenantsRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant',
  component: TenantPage,
})
const superPoisRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/pois',
  component: PoiListPage,
})
const superToursRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tours',
  component: TourListPage,
})
const superSurveysRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/surveys',
  component: SurveyListPage,
})
const superSurveyResponsesRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/surveys/responses',
  component: SurveyResponsesPage,
})
const superDestinationsRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/destinations',
  component: DestinationListPage,
})
const superTenantDetailRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$id',
  component: TenantDetailPage,
})
const superPoiListRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/pois',
  component: PoiListPage,
})
const superTenantPoiCategoriesRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/poi-categories',
  component: PoiCategoryListPage,
})
const superTenantPoiCategoryCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/poi-categories/create',
  component: PoiCategoryCreatePage,
})
const superTenantPoiCategoryEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/poi-categories/$categoryId/edit',
  component: PoiCategoryEditPage,
})
const superTenantDestinationRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/destinations',
  component: TenantDestinationPage,
})
const superPoiCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/pois/create',
  component: PoiFormPage,
})
const superPoiEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/pois/$poiId/edit',
  component: PoiFormPage,
})
const superTourListRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/tours',
  component: TourListPage,
})
const superTourCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/tours/create',
  component: TourFormPage,
})
const superTourEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/tours/$tourId/edit',
  component: TourFormPage,
})
const superSurveyTenantListRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys',
  component: SurveyListPage,
})
const superSurveyTenantResponsesRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys/responses',
  component: SurveyResponsesPage,
})
const superSurveyCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys/create',
  component: SurveyVersionShellPage,
})
const superSurveyQuestionCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys/$versionId/questions/create',
  component: SurveyQuestionPage,
})
const superSurveyQuestionRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys/$versionId/questions/$questionId',
  component: SurveyQuestionPage,
})
const superSurveyVersionRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/surveys/$versionId',
  component: SurveyVersionShellPage,
})
const superTicketsRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tickets',
  component: TicketListPage,
})
const superTicketDetailRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tickets/$id',
  component: TicketDetailPage,
})
const superTicketFeaturesRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/ticket-features',
  component: TicketFeatureListPage,
})
const superTicketConfigsRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/ticket-configs',
  component: TicketConfigListPage,
})
const superTicketConfigCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/ticket-configs/create',
  component: TicketConfigFormPage,
})
const superTenantTicketConfigsRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/ticket-configs',
  component: TicketConfigListPage,
})
const superTenantTicketConfigCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/ticket-configs/create',
  component: TicketConfigFormPage,
})
const superTenantTicketConfigEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/ticket-configs/$id/edit',
  component: TicketConfigFormPage,
})
const superVouchersRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/vouchers',
  component: VoucherListPage,
})
const superVoucherListRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/vouchers',
  component: VoucherListPage,
})
const superVoucherCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/vouchers/create',
  component: VoucherFormPage,
})
const superVoucherEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/tenant/$tenantId/vouchers/$voucherId/edit',
  component: VoucherFormPage,
})
const superDestinationCreateRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/destinations/create',
  component: DestinationFormPage,
})
const superDestinationEditRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/destinations/$destinationId/edit',
  component: DestinationFormPage,
})
const superUsersRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/users',
  component: UserListPage,
})
const superUserDetailRoute = createRoute({
  getParentRoute: () => superProtectedRoute,
  path: '/super/users/$id',
  component: UserDetailPage,
})

const tenantDashboardRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin',
  component: DashboardPage,
})
const tenantTenantsRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant',
  component: TenantPage,
})
const tenantPoisRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/pois',
  component: PoiListPage,
})
const tenantToursRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tours',
  component: TourListPage,
})
const tenantSurveysRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/surveys',
  component: SurveyListPage,
})
const tenantSurveyResponsesRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/surveys/responses',
  component: SurveyResponsesPage,
})
const tenantDestinationsRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/destinations',
  component: DestinationListPage,
})
const tenantTenantDetailRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$id',
  component: TenantDetailPage,
})
const tenantPoiListRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/pois',
  component: PoiListPage,
})
const tenantTenantPoiCategoriesRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/poi-categories',
  component: PoiCategoryListPage,
})
const tenantTenantPoiCategoryCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/poi-categories/create',
  component: PoiCategoryCreatePage,
})
const tenantTenantPoiCategoryEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/poi-categories/$categoryId/edit',
  component: PoiCategoryEditPage,
})
const tenantTenantDestinationRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/destinations',
  component: TenantDestinationPage,
})
const tenantPoiCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/pois/create',
  component: PoiFormPage,
})
const tenantPoiEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/pois/$poiId/edit',
  component: PoiFormPage,
})
const tenantTourListRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/tours',
  component: TourListPage,
})
const tenantTourCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/tours/create',
  component: TourFormPage,
})
const tenantTourEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/tours/$tourId/edit',
  component: TourFormPage,
})
const tenantSurveyTenantListRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys',
  component: SurveyListPage,
})
const tenantSurveyTenantResponsesRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys/responses',
  component: SurveyResponsesPage,
})
const tenantSurveyCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys/create',
  component: SurveyVersionShellPage,
})
const tenantSurveyQuestionCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys/$versionId/questions/create',
  component: SurveyQuestionPage,
})
const tenantSurveyQuestionRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys/$versionId/questions/$questionId',
  component: SurveyQuestionPage,
})
const tenantSurveyVersionRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/surveys/$versionId',
  component: SurveyVersionShellPage,
})
const tenantDestinationCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/destinations/create',
  component: DestinationFormPage,
})
const tenantDestinationEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/destinations/$destinationId/edit',
  component: DestinationFormPage,
})
const tenantTicketConfigsRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/ticket-configs',
  component: TicketConfigListPage,
})
const tenantTicketConfigCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/ticket-configs/create',
  component: TicketConfigFormPage,
})
const tenantTicketConfigEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/ticket-configs/$id/edit',
  component: TicketConfigFormPage,
})
const tenantVouchersRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/vouchers',
  component: VoucherListPage,
})
const tenantVoucherListRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/vouchers',
  component: VoucherListPage,
})
const tenantVoucherCreateRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/vouchers/create',
  component: VoucherFormPage,
})
const tenantVoucherEditRoute = createRoute({
  getParentRoute: () => tenantProtectedRoute,
  path: '/$tenantSlug/admin/tenant/$tenantId/vouchers/$voucherId/edit',
  component: VoucherFormPage,
})

export const routeTree = rootRoute.addChildren([
  superLoginRoute,
  tenantLoginRoute,
  superProtectedRoute.addChildren([
    superDashboardRoute,
    superTenantsRoute,
    superPoisRoute,
    superToursRoute,
    superSurveysRoute,
    superSurveyResponsesRoute,
    superDestinationsRoute,
    superTenantDetailRoute,
    superPoiListRoute,
    superTenantPoiCategoriesRoute,
    superTenantPoiCategoryCreateRoute,
    superTenantPoiCategoryEditRoute,
    superTenantDestinationRoute,
    superPoiCreateRoute,
    superPoiEditRoute,
    superTourListRoute,
    superTourCreateRoute,
    superTourEditRoute,
    superSurveyTenantListRoute,
    superSurveyTenantResponsesRoute,
    superSurveyCreateRoute,
    superSurveyQuestionCreateRoute,
    superSurveyQuestionRoute,
    superSurveyVersionRoute,
    superDestinationCreateRoute,
    superDestinationEditRoute,
    superUsersRoute,
    superUserDetailRoute,
    superVouchersRoute,
    superVoucherListRoute,
    superVoucherCreateRoute,
    superVoucherEditRoute,
    superTicketsRoute,
    superTicketDetailRoute,
    superTicketFeaturesRoute,
    superTicketConfigsRoute,
    superTicketConfigCreateRoute,
    superTenantTicketConfigsRoute,
    superTenantTicketConfigCreateRoute,
    superTenantTicketConfigEditRoute,
  ]),
  tenantProtectedRoute.addChildren([
    tenantDashboardRoute,
    tenantTenantsRoute,
    tenantPoisRoute,
    tenantToursRoute,
    tenantSurveysRoute,
    tenantSurveyResponsesRoute,
    tenantDestinationsRoute,
    tenantTenantDetailRoute,
    tenantPoiListRoute,
    tenantTenantPoiCategoriesRoute,
    tenantTenantPoiCategoryCreateRoute,
    tenantTenantPoiCategoryEditRoute,
    tenantTenantDestinationRoute,
    tenantPoiCreateRoute,
    tenantPoiEditRoute,
    tenantTourListRoute,
    tenantTourCreateRoute,
    tenantTourEditRoute,
    tenantSurveyTenantListRoute,
    tenantSurveyTenantResponsesRoute,
    tenantSurveyCreateRoute,
    tenantSurveyQuestionCreateRoute,
    tenantSurveyQuestionRoute,
    tenantSurveyVersionRoute,
    tenantDestinationCreateRoute,
    tenantDestinationEditRoute,
    tenantVouchersRoute,
    tenantVoucherListRoute,
    tenantVoucherCreateRoute,
    tenantVoucherEditRoute,
    tenantTicketConfigsRoute,
    tenantTicketConfigCreateRoute,
    tenantTicketConfigEditRoute,
  ]),
])
